
import { useCallback, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Scale, X } from "lucide-react";
import { JL_ORIGIN, formatPrice, titleCase } from "../../lib/jlApi";
import {
  pricePerSqFt,
  parseDaysAgo,
  nearestBeach,
  MAX_COMPARE,
  type CompareEntry,
} from "./listingUtils";

interface CompareTrayProps {
  entries: CompareEntry[];
  onRemove: (id: number) => void;
  onClear: () => void;
  // Controlled so the toolbar "Compare" pill can also open the dialog.
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// One comparison attribute rendered for each home. `beach` is the only computed
// value and is labeled "approx. straight-line" so it's never mistaken for a
// drive distance.
const SPEC_ROWS: {
  key: string;
  label: string;
  value: (e: CompareEntry) => string;
}[] = [
  { key: "price", label: "Price", value: (e) => e.listing.priceDisplay || formatPrice(e.listing.price) },
  {
    key: "ppsf",
    label: "$/sqft",
    value: (e) => {
      const p = pricePerSqFt(e.listing);
      return p ? `$${p.toLocaleString()}` : "\u2014";
    },
  },
  { key: "bed", label: "Beds", value: (e) => e.listing.bed || "\u2014" },
  { key: "bath", label: "Baths", value: (e) => e.listing.bath || "\u2014" },
  {
    key: "sqft",
    label: "Sq Ft",
    value: (e) => (e.listing.sqFtTotal ? `${e.listing.sqFtTotal}` : "\u2014"),
  },
  {
    key: "sub",
    label: "Community",
    value: (e) =>
      e.listing.subdivision ? titleCase(e.listing.subdivision) : "\u2014",
  },
  {
    key: "days",
    label: "Days listed",
    value: (e) => {
      const d = parseDaysAgo(e.listing.daysListedText);
      return d === null ? "\u2014" : d === 0 ? "Today" : `${d} days`;
    },
  },
  {
    key: "beach",
    label: "Nearest beach",
    value: (e) => {
      const nb = nearestBeach(e.lat, e.lng);
      return nb ? `${nb.miles.toFixed(1)} mi \u00b7 ${nb.name}` : "\u2014";
    },
  },
];

export default function CompareTray({
  entries,
  onRemove,
  onClear,
  open,
  onOpenChange,
}: CompareTrayProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  // Dialog a11y: lock body scroll, move focus into the dialog, trap Tab focus,
  // close on Escape, and restore focus on close (mirrors PhotoLightbox.tsx).
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      } else if (e.key === "Tab") {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, close]);

  if (entries.length === 0) return null;

  return (
    <>
      {/* Docked tray: above the bottom-sheet peek on mobile, bottom-right on
          desktop. Absolute within the map section so it never covers the
          viewport-fixed MobileContactBar. */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="pointer-events-auto absolute inset-x-2 bottom-[108px] z-[1100] lg:inset-x-auto lg:bottom-4 lg:right-4 lg:w-auto"
      >
        <div className="flex items-center gap-2 rounded-2xl border border-[var(--gold)]/35 bg-white/95 p-2 shadow-[0_8px_30px_rgba(30,58,95,.18)] backdrop-blur">
          <div className="flex flex-1 items-center gap-2 overflow-x-auto lg:flex-none">
            {entries.map((e) => (
              <div
                key={e.listing.id}
                className="relative flex-shrink-0"
                title={`${titleCase(e.listing.street)}, ${titleCase(e.listing.city)}`}
              >
                {e.listing.imageUrl ? (
                  <img
                    src={`${JL_ORIGIN}${e.listing.imageUrl}`}
                    alt=""
                    className="h-12 w-16 rounded-lg object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-12 w-16 items-center justify-center rounded-lg bg-[var(--sand)] text-[10px] text-[var(--slate)]">
                    {formatPrice(e.listing.price)}
                  </div>
                )}
                <button
                  onClick={() => onRemove(e.listing.id)}
                  aria-label="Remove from compare"
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--navy)] text-white shadow active:scale-90"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {/* Empty slots hint how many more can be added. */}
            {Array.from({ length: MAX_COMPARE - entries.length }).map((_, i) => (
              <div
                key={`slot-${i}`}
                className="flex h-12 w-16 flex-shrink-0 items-center justify-center rounded-lg border border-dashed border-[var(--gold)]/50 bg-[var(--cream)]/60 text-[10px] font-medium text-[var(--slate)]"
              >
                + Add
              </div>
            ))}
          </div>

          <div className="flex flex-shrink-0 flex-col gap-1">
            <button
              onClick={() => onOpenChange(true)}
              disabled={entries.length < 2}
              className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white transition-all active:scale-95 ${
                entries.length >= 2
                  ? "bg-gradient-to-r from-[var(--gold)] to-[#b8934f] shadow-[0_2px_12px_rgba(197,165,114,.5)] hover:brightness-110"
                  : "bg-[var(--navy)] opacity-40"
              }`}
            >
              <Scale size={13} /> Compare ({entries.length})
            </button>
            <button
              onClick={onClear}
              className="text-[11px] font-medium text-[var(--slate)] hover:text-[var(--navy)]"
            >
              Clear
            </button>
          </div>
        </div>
      </motion.div>

      {/* Comparison overlay */}
      <AnimatePresence>
        {open && entries.length >= 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[20050] flex flex-col bg-black/60 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Compare homes"
            onClick={close}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
              ref={dialogRef}
              tabIndex={-1}
              onClick={(ev) => ev.stopPropagation()}
              className="mx-auto mt-auto flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl outline-none lg:mb-auto lg:mt-auto lg:max-h-[88vh] lg:rounded-3xl"
            >
              <div className="flex items-center justify-between border-b border-[var(--sand)] px-4 py-3">
                <h2
                  className="flex items-center gap-2 text-lg font-semibold text-[var(--navy)]"
                  style={{ fontFamily: "var(--font-playfair)" }}
                >
                  <Scale size={18} className="text-[var(--gold)]" />
                  Compare {entries.length} homes
                </h2>
                <button
                  onClick={close}
                  aria-label="Close compare"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--slate)] hover:bg-[var(--sand)] active:scale-90"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-auto">
                <CompareGrid entries={entries} onRemove={onRemove} />
                <p className="px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-2 text-[11px] leading-snug text-[var(--slate)]">
                  Beach distance is approximate straight-line (&ldquo;as the crow
                  flies&rdquo;), not drive time. Details from the MLS via Jack
                  Lingo &mdash; verify current status before making decisions.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// The side-by-side (desktop) / swipeable (mobile) comparison body. Each home is
// a column; specs share aligned rows via a consistent order and fixed layout.
function CompareGrid({
  entries,
  onRemove,
}: {
  entries: CompareEntry[];
  onRemove: (id: number) => void;
}) {
  return (
    <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto p-3 lg:snap-none">
      {entries.map((e) => (
        <div
          key={e.listing.id}
          className="w-[78%] flex-shrink-0 snap-center overflow-hidden rounded-2xl border border-[var(--sand)] sm:w-[60%] lg:w-0 lg:flex-1 lg:snap-align-none"
        >
          {/* Column header: photo + price + address */}
          <div className="relative">
            {e.listing.imageUrl ? (
              <img
                src={`${JL_ORIGIN}${e.listing.imageUrl}`}
                alt={`${titleCase(e.listing.street)}, ${titleCase(e.listing.city)}`}
                className="h-32 w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="h-32 w-full bg-[var(--sand)]" />
            )}
            <button
              onClick={() => onRemove(e.listing.id)}
              aria-label="Remove from compare"
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[var(--navy)] shadow active:scale-90"
            >
              <X size={16} />
            </button>
          </div>
          <div className="border-b border-[var(--sand)] p-3">
            <p
              className="text-lg font-semibold text-[var(--navy)]"
              style={{ fontFamily: "var(--font-playfair)" }}
            >
              {e.listing.priceDisplay || formatPrice(e.listing.price)}
            </p>
            <p className="mt-0.5 text-xs font-medium text-[var(--navy)]">
              {titleCase(e.listing.street)}
            </p>
            <p className="text-xs text-[var(--slate)]">
              {titleCase(e.listing.city)}, {e.listing.state}
            </p>
          </div>

          {/* Spec rows (aligned by consistent order + fixed min height) */}
          <dl className="text-sm">
            {SPEC_ROWS.map((row) => (
              <div
                key={row.key}
                className="flex min-h-[44px] items-center justify-between gap-2 border-b border-[var(--sand)] px-3 py-2 last:border-b-0"
              >
                <dt className="text-xs text-[var(--slate)]">{row.label}</dt>
                <dd className="text-right font-medium text-[var(--navy)]">
                  {row.value(e)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}
